/**
 * Cart Drawer Recommendations
 * Handles dynamic product recommendations based on cart items
 */

class CartDrawerRecommendations {
  constructor() {
    this.container = document.querySelector('.cart-drawer-recommendations');
    this.productsContainer = this.container?.querySelector('.cart-drawer-recommendations__products');
    this.loadingContainer = this.container?.querySelector('.cart-drawer-recommendations__loading');
    this.fallbackContainer = this.container?.querySelector('.cart-drawer-recommendations__fallback');
    this.sectionId = this.container?.dataset.sectionId;
    this.isInitialized = false;
    this.isLoading = false;
    this.hasLoadedRecommendations = false;
    this.preservedState = null;
    this.lastCartProductIds = ''; // Track previous cart product IDs
    this.pendingCartProductIds = null; // Store product IDs when container doesn't exist
    this.cartUpdateUnsubscriber = null; // For pub/sub cleanup
    this.isQuantityOnlyChange = false; // Flag to track quantity-only changes
    
    this.init();

    // Note: Event listeners are now handled in the init() method to avoid duplicates
  }

  init() {
    if (!this.container) return;
    
    // Prevent multiple initializations
    if (this.isInitialized) return;
    this.isInitialized = true;
    
    // Show recommendations when cart drawer opens (only if not already loaded)
    document.addEventListener('cart-drawer:opened', (event) => {
      // Re-find container elements in case they were re-rendered
      this.container = document.querySelector('.cart-drawer-recommendations');
      this.loadingContainer = this.container?.querySelector('.cart-drawer-recommendations__loading');
      this.productsContainer = this.container?.querySelector('.cart-drawer-recommendations__products');
      this.fallbackContainer = this.container?.querySelector('.cart-drawer-recommendations__fallback');
      
      // Check if we have cart product IDs and load recommendations if needed
      let cartProductIds = this.container?.dataset.cartProductIds;
      
      // If no cart product IDs in container but we have pending ones, use those
      if ((!cartProductIds || cartProductIds.trim() === '') && this.pendingCartProductIds) {
        cartProductIds = this.pendingCartProductIds;
        this.container.dataset.cartProductIds = cartProductIds;
        this.pendingCartProductIds = null; // Clear pending IDs
      }
      
      if (this.container && cartProductIds && cartProductIds.trim() !== '') {
        // Initialize the last cart product IDs when drawer opens
        if (!this.lastCartProductIds) {
          this.lastCartProductIds = cartProductIds;
        }
        
        // Ensure container is visible when there are cart items
        this.ensureContainerVisible();
        
        // Check if recommendations are already visible and properly rendered
        const isAlreadyVisible = this.isRecommendationsVisible();
        
        if (isAlreadyVisible) {
          this.initSwiper();
          this.addCartButtonListeners();
        } else {
          this.loadRecommendations();
        }
      } else {
        // Also ensure Swiper is working if recommendations are already visible
        setTimeout(() => {
          if (this.container && this.container.style.display !== 'none') {
            const swiperContainer = this.container.querySelector('.cart-drawer-recommendations-swiper');
            if (swiperContainer && !this.swiperInstance) {
              this.initSwiper();
            }
          }
        }, 200);
      }
    });

    // Update recommendations when cart changes (only if product composition changes)
    document.addEventListener('cart:updated', (event) => {
      // Reset any stuck buttons first
      this.resetStuckButtons();
      
      if (event.detail && event.detail.cart && event.detail.cart.items && Array.isArray(event.detail.cart.items)) {
        // Check if the product composition has changed (not just quantities)
        const currentProductIds = event.detail.cart.items.map(item => item.product_id).join(',');
        
        if (currentProductIds !== this.lastCartProductIds) {
          // Product composition changed, update recommendations
          this.isQuantityOnlyChange = false;
          this.lastCartProductIds = currentProductIds;
          this.updateCartProductIds(event.detail.cart);
        } else {
          // Only re-initialize Swiper and event listeners, don't reload recommendations
          setTimeout(() => {
            this.reinitializeSwiperAndListeners();
          }, 100);
        }
      } else {
        // If no cart data or invalid structure, check if container exists and has product IDs
        if (this.container && this.container.dataset.cartProductIds) {
          this.loadRecommendations();
        } else {
          this.hideRecommendations();
        }
      }
    });

    // Also listen to the pub/sub cart update event as a fallback
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source !== 'cart-drawer-recommendations') {
          // Reset any stuck buttons first
          this.resetStuckButtons();
          
          // Check if this is a quantity-only change
          if (event.cartData && event.cartData.items && Array.isArray(event.cartData.items)) {
            const currentProductIds = event.cartData.items.map(item => item.product_id).join(',');
            const isQuantityOnlyChange = currentProductIds === this.lastCartProductIds;
            
            if (isQuantityOnlyChange) {
              this.isQuantityOnlyChange = true;
            } else {
              this.updateCartProductIds(event.cartData.items);
            }
          }
          
          // Re-initialize Swiper and listeners when cart is updated from other sources
          setTimeout(() => {
            this.reinitializeSwiperAndListeners();
          }, 100);
        }
      });
    }

    // Re-initialize when cart drawer is re-rendered
    document.addEventListener('cart-drawer:rendered', (event) => {
      // Check if we have cart data and update product IDs
      if (event.detail && event.detail.cartData) {
        this.updateCartProductIds(event.detail.cartData);
      }
      
      setTimeout(() => {
        // Reset any stuck buttons first
        this.resetStuckButtons();
        
        // Re-find container elements in case they were re-rendered
        this.container = document.querySelector('.cart-drawer-recommendations');
        this.loadingContainer = this.container?.querySelector('.cart-drawer-recommendations__loading');
        this.productsContainer = this.container?.querySelector('.cart-drawer-recommendations__products');
        this.fallbackContainer = this.container?.querySelector('.cart-drawer-recommendations__fallback');
        
        // Preserve lastCartProductIds if container has cart product IDs
        if (this.container && this.container.dataset.cartProductIds && !this.lastCartProductIds) {
          this.lastCartProductIds = this.container.dataset.cartProductIds;
        }
        
        // Check if recommendations are already visible and properly rendered
        const isAlreadyVisible = this.isRecommendationsVisible();
        
        if (isAlreadyVisible) {
          // Just reinitialize Swiper and event listeners if recommendations are already visible
          this.initSwiper();
          // Also ensure event listeners are added even if Swiper fails
          setTimeout(() => {
            this.addCartButtonListeners();
          }, 100);
        } else {
          // Load recommendations if they're not visible and it's not a quantity-only change
          if (this.isQuantityOnlyChange) {
            this.isQuantityOnlyChange = false; // Reset the flag
          } else {
            this.loadRecommendations();
          }
        }
      }, 150);
    });

    // Listen for section updates (for when the cart drawer section is updated)
    document.addEventListener('shopify:section:load', (event) => {
      if (event.detail.sectionId === 'cart-drawer') {
        setTimeout(() => {
          this.reinitialize();
        }, 100);
      }
    });

    // Initial load if cart has items and drawer is already open
    if (this.container.dataset.cartProductIds && document.querySelector('cart-drawer').classList.contains('active')) {
      // Initialize the last cart product IDs
      this.lastCartProductIds = this.container.dataset.cartProductIds;
      this.loadRecommendations();
    }
  }

  async loadRecommendations() {
    if (!this.container || !this.productsContainer) {
      return;
    }

    // Prevent multiple simultaneous loads
    if (this.isLoading) {
      return;
    }

    const cartProductIds = this.container.dataset.cartProductIds;
    
    if (!cartProductIds) {
      this.hideRecommendations();
      this.lastCartProductIds = '';
      return;
    }

    // Initialize lastCartProductIds if not set
    if (!this.lastCartProductIds) {
      this.lastCartProductIds = cartProductIds;
    }

    // Ensure container is visible and remove any inline display:none
    this.container.style.display = 'block';
    this.container.style.removeProperty('display'); // Remove any inline display styles
    
    this.isLoading = true;
    this.showLoading();

    try {
      // Get the first product ID from cart for recommendations
      const firstProductId = cartProductIds.split(',')[0];
      
      // Fetch recommendations from Shopify
      const response = await fetch(`/recommendations/products.json?product_id=${firstProductId}&limit=6`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch recommendations');
      }

      const data = await response.json();
      
      if (data.products && data.products.length > 0) {
        this.renderRecommendations(data.products);
        this.hasLoadedRecommendations = true;
      } else {
        this.showFallback();
        this.hasLoadedRecommendations = true;
      }
    } catch (error) {
      this.showFallback();
      this.hasLoadedRecommendations = true;
    } finally {
      this.isLoading = false;
    }
  }

  renderRecommendations(products) {
    if (!this.productsContainer) {
      return;
    }

    // Filter out products already in cart
    const cartProductIds = this.container.dataset.cartProductIds.split(',');
    
    const filteredProducts = products.filter(product => 
      !cartProductIds.includes(product.id.toString())
    );

    if (filteredProducts.length === 0) {
      this.showFallback();
      return;
    }

    // Create slides for each product (showing first variant only)
    const variantSlides = [];
    
    filteredProducts.slice(0, this.getRecommendationsLimit()).forEach(product => {
      // Always use the first variant
      const firstVariant = product.variants[0];
      const slide = this.renderVariantSlide(product, firstVariant);
      variantSlides.push(slide);
    });
    
    // Create Swiper container programmatically
    const swiperContainer = document.createElement('div');
    swiperContainer.className = 'swiper cart-drawer-recommendations-swiper';
    
    const swiperWrapper = document.createElement('div');
    swiperWrapper.className = 'swiper-wrapper';
    
    // Create slides programmatically
    variantSlides.forEach((slide, index) => {
      const slideElement = document.createElement('div');
      slideElement.className = 'swiper-slide cart-drawer-recommendations-slide';
      slideElement.innerHTML = slide;
      swiperWrapper.appendChild(slideElement);
    });
    
    swiperContainer.appendChild(swiperWrapper);

    // Clear container first
    this.productsContainer.innerHTML = '';
    
    // Append the programmatically created Swiper container
    this.productsContainer.appendChild(swiperContainer);
    
    this.hideLoading();
    this.showProducts();
    
    // Initialize Swiper immediately
    this.initSwiper();
  }

  renderVariantSlide(product, variant) {
    const image = product.featured_image || (product.images && product.images[0]);
    const imageUrl = image ? image.replace('.jpg', '_200x200.jpg') : '';
    
    // Generate variant title
    let variantTitle = product.title;
    if (product.variants.length > 1) {
      const variantOptions = variant.options.join(' / ');
      variantTitle = `${product.title} - ${variantOptions}`;
    }
    
    return `
      <div class="cart-drawer-product-card">
        <a href="${product.url}" class="cart-drawer-product-card__link">
          <div class="cart-drawer-product-card__image">
            ${imageUrl ? `<img src="${imageUrl}" alt="${variantTitle}" loading="lazy" width="100" height="100">` : ''}
          </div>
          <div class="cart-drawer-product-card__info">
            <h4 class="cart-drawer-product-card__title">${variantTitle}</h4>
            <div class="cart-drawer-product-card__price">
              ${this.formatPrice(variant.price, variant.compare_at_price)}
            </div>
          </div>
        </a>
        <form method="post" action="/cart/add" class="cart-drawer-product-form">
          <input type="hidden" name="id" value="${variant.id}">
          <input type="hidden" name="quantity" value="1">
          <button 
            type="submit"
            class="cart-drawer-product-card__add button button--small"
            ${!variant.available ? 'disabled' : ''}
            aria-label="Add ${variantTitle} to cart"
          >
            ${variant.available ? 'Add' : 'Sold out'}
          </button>
        </form>
      </div>
    `;
  }

  formatPrice(price, compareAtPrice) {
    // Use Shopify's currency or default to GBP
    let currency = 'GBP';
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      currency = window.Shopify.currency.active;
    }
    // Use the shop's locale or default to en-GB
    let locale = 'en-GB';
    if (window.Shopify && window.Shopify.locale) {
      locale = window.Shopify.locale;
    }
    
    const moneyFormatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency
    });
    const formattedPrice = moneyFormatter.format(price / 100);
    const formattedComparePrice = compareAtPrice ? moneyFormatter.format(compareAtPrice / 100) : null;
    
    if (formattedComparePrice && compareAtPrice > price) {
      return `
        <span class="price price--sale">${formattedPrice}</span>
        <span class="price price--compare">${formattedComparePrice}</span>
      `;
    }
    
    return `<span class="price">${formattedPrice}</span>`;
  }

  async addToCart(button) {
    const variantId = button.dataset.variantId;
    const productId = button.dataset.productId;
    
    if (!variantId) {
      console.error('CartDrawerRecommendations: No variant ID found');
      return;
    }

    // Disable button and show loading state
    button.disabled = true;
    button.textContent = 'Adding...';

    try {
      // Get the cart drawer element
      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer) {
        console.error('CartDrawerRecommendations: Cart drawer not found');
        throw new Error('Cart drawer not found');
      }

      // Use the same approach as product form
      if (typeof fetchConfig === 'undefined') {
        console.error('CartDrawerRecommendations: fetchConfig function is not available');
        throw new Error('fetchConfig function not available');
      }
      
      const config = fetchConfig('javascript');
      
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 1);
      
      // Add sections for cart drawer update
      const sectionsToRender = cartDrawer.getSectionsToRender();
      
      // Check if getSectionsToRender method exists and returns valid data
      if (!sectionsToRender || !Array.isArray(sectionsToRender)) {
        console.error('CartDrawerRecommendations: Invalid sections to render', sectionsToRender);
        throw new Error('Invalid sections configuration');
      }
      
      // Convert sections to the correct format for the API
      const sectionIds = sectionsToRender.map((section) => section.id).join(',');
      
      formData.append('sections', sectionIds);
      formData.append('sections_url', window.location.pathname);
      
      config.body = formData;

      const response = await fetch(`${window.routes.cart_add_url}`, config);

      if (!response.ok) {
        console.error('CartDrawerRecommendations: Response not ok', {
          status: response.status,
          statusText: response.statusText
        });
        throw new Error('Failed to add to cart');
      }

      const cartData = await response.json();
      
      // Use the same cart update system as product form
      if (cartData.status) {
        console.error('CartDrawerRecommendations: Cart data has error status', cartData);
        // Handle error
        throw new Error(cartData.description || 'Failed to add to cart');
      }

      // Check if the required sections are available
      if (!cartData.sections || !cartData.sections['cart-drawer']) {
        console.error('CartDrawerRecommendations: Missing cart-drawer section in response', {
          sections: cartData.sections,
          availableSections: cartData.sections ? Object.keys(cartData.sections) : null
        });
        
        // Fallback: trigger a cart refresh instead of trying to render contents
        
        // Use the same approach as product form - publish cart update event
        if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-drawer-recommendations',
            productVariantId: variantId,
            cartData: cartData,
          });
        } else {
          // Fallback to custom event if pub/sub is not available
          document.dispatchEvent(new CustomEvent('cart:updated', {
            detail: { cart: cartData }
          }));
        }
        
        return; // Exit early, don't try to render contents
      }

      // Update cart drawer contents using the same method as product form
      
      // Try to update cart drawer with retry mechanism
      let retryCount = 0;
      const maxRetries = 3;
      let cartDrawerUpdated = false;
      
      while (retryCount < maxRetries && !cartDrawerUpdated) {
        try {
          // Re-find the cart drawer element in case it was re-rendered during locale change
          const currentCartDrawer = document.querySelector('cart-drawer');
          if (!currentCartDrawer) {
            console.error(`CartDrawerRecommendations: Cart drawer not found after re-finding (attempt ${retryCount + 1})`);
            retryCount++;
            if (retryCount < maxRetries) {
              await new Promise(resolve => setTimeout(resolve, 100)); // Wait 100ms before retry
              continue;
            }
            throw new Error('Cart drawer not found after re-finding');
          }
          
          if (typeof currentCartDrawer.renderContents !== 'function') {
            console.error(`CartDrawerRecommendations: renderContents method not available on cart drawer (attempt ${retryCount + 1})`);
            retryCount++;
            if (retryCount < maxRetries) {
              await new Promise(resolve => setTimeout(resolve, 100)); // Wait 100ms before retry
              continue;
            }
            throw new Error('Cart drawer renderContents method not available');
          }
          
          currentCartDrawer.renderContents(cartData);
          cartDrawerUpdated = true;
          
        } catch (error) {
          console.error(`CartDrawerRecommendations: Error updating cart drawer (attempt ${retryCount + 1})`, error);
          retryCount++;
          if (retryCount < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 100)); // Wait 100ms before retry
          } else {
            throw error;
          }
        }
      }

      // Trigger cart update event for other components
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cart: cartData }
      }));
      
      // Reset button state after successful completion
      setTimeout(() => {
        // Find the button again in case the DOM was re-rendered
        const currentButton = this.container?.querySelector(`[data-variant-id="${variantId}"]`);
        if (currentButton && currentButton.textContent === 'Adding...') {
          currentButton.textContent = 'Add';
          currentButton.disabled = false;
        }
      }, 100);

    } catch (error) {
      console.error('CartDrawerRecommendations: Error in addToCart', {
        error: error.message,
        stack: error.stack,
        variantId,
        productId
      });
      
      button.textContent = 'Error';
      setTimeout(() => {
        button.textContent = 'Add';
        button.disabled = false;
      }, 2000);
    }
  }

  showLoading() {
    if (this.loadingContainer) {
      this.loadingContainer.style.display = 'flex';
    }
    this.hideProducts();
    this.hideFallback();
  }

  hideLoading() {
    if (this.loadingContainer) {
      this.loadingContainer.style.display = 'none';
    }
  }

  showProducts() {
    if (this.productsContainer) {
      this.productsContainer.style.display = 'block';
    }
    // Ensure main container is visible and remove any inline display:none
    if (this.container) {
      this.container.style.display = 'block';
      this.container.style.removeProperty('display'); // Remove any inline display styles
    }
  }

  hideProducts() {
    if (this.productsContainer) {
      this.productsContainer.style.display = 'none';
    }
    // Clean up Swiper instance
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }
  }

  showFallback() {
    if (this.fallbackContainer) {
      this.fallbackContainer.style.display = 'block';
    }
    this.hideLoading();
    this.hideProducts();
    // Ensure main container is visible and remove any inline display:none
    if (this.container) {
      this.container.style.display = 'block';
      this.container.style.removeProperty('display'); // Remove any inline display styles
    }
    
    // Initialize Swiper for fallback content
    this.initSwiper();
  }

  hideFallback() {
    if (this.fallbackContainer) {
      this.fallbackContainer.style.display = 'none';
    }
  }

  hideRecommendations() {
    this.container.style.display = 'none';
  }

  updateCartProductIds(cartItems) {
    let productIds = '';
    
    // Handle different data structures
    if (Array.isArray(cartItems)) {
      // Standard cart items array
      productIds = cartItems.map(item => {
        return item.product_id;
      }).join(',');
    } else if (cartItems && typeof cartItems === 'object') {
      // Single item object (like from cart-drawer:rendered event)
      if (cartItems.product_id) {
        productIds = cartItems.product_id.toString();
      } else if (cartItems.items && Array.isArray(cartItems.items)) {
        // Cart object with items array
        productIds = cartItems.items.map(item => item.product_id).join(',');
      } else {
        // If no valid cart items, hide recommendations
        if (this.container) {
          this.hideRecommendations();
        }
        this.lastCartProductIds = '';
        return;
      }
    } else {
      // If no valid cart items, hide recommendations
      if (this.container) {
        this.hideRecommendations();
      }
      this.lastCartProductIds = '';
      return;
    }
    
    // Update the tracked product IDs
    this.lastCartProductIds = productIds;
    
    // Try to find the container (it might not exist if cart drawer is closed)
    this.container = document.querySelector('.cart-drawer-recommendations');
    
    if (this.container) {
      this.container.dataset.cartProductIds = productIds;
      
      if (productIds && productIds.trim() !== '') {
        // Show recommendations container and ensure it's visible
        this.ensureContainerVisible();
        
        // Only reload recommendations if it's not a quantity-only change
        if (!this.isQuantityOnlyChange) {
          // Reset the loaded flag to allow reloading
          this.hasLoadedRecommendations = false;
          // Only reload if not already loading
          if (!this.isLoading) {
            this.loadRecommendations();
          }
        } else {
          // Don't reset the flag here - let it be reset in reinitializeSwiperAndListeners
        }
      } else {
        this.hideRecommendations();
      }
    } else {
      // Store the product IDs so they can be used when the cart drawer opens
      this.pendingCartProductIds = productIds;
    }
  }

  updateSwiper() {
    if (this.swiperInstance) {
      this.swiperInstance.update();
      this.swiperInstance.updateSlides();
    } else {
      // If no Swiper instance, try to reinitialize
      setTimeout(() => this.initSwiper(), 100);
    }
  }

  getRecommendationsLimit() {
    // Get the limit from Shopify settings or default to 4
    const limitElement = document.querySelector('[data-cart-recommendations-limit]');
    if (limitElement) {
      return parseInt(limitElement.dataset.cartRecommendationsLimit) || 4;
    }
    return 4;
  }

  isRecommendationsVisible() {
    if (!this.container) return false;
    
    const swiperContainer = this.container.querySelector('.cart-drawer-recommendations-swiper');
    const hasSwiperContainer = swiperContainer && swiperContainer.querySelector('.swiper-wrapper') && swiperContainer.querySelectorAll('.swiper-slide').length > 0;
    
    return (this.productsContainer?.style.display !== 'none' || 
            this.fallbackContainer?.style.display !== 'none') && hasSwiperContainer;
  }

  ensureContainerVisible() {
    if (this.container && this.container.dataset.cartProductIds && this.container.dataset.cartProductIds.trim() !== '') {
      this.container.style.display = 'block';
      this.container.style.removeProperty('display'); // Remove any inline display styles
      
      // Double-check after a brief delay to ensure it's visible
      setTimeout(() => {
        if (this.container && this.container.style.display === 'none') {
          this.container.style.display = 'block';
        }
      }, 0);
    }
  }

  initSwiper(retryCount = 0) {
    // Prevent infinite retry loop
    if (retryCount > 10) {
      return;
    }

    // Wait for Swiper to be available
    if (typeof window.swiper === 'undefined') {
      setTimeout(() => this.initSwiper(retryCount + 1), 100);
      return;
    }

    // Destroy existing Swiper instance if it exists
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }

    const swiperContainer = this.container.querySelector('.cart-drawer-recommendations-swiper');
    if (!swiperContainer) {
      return;
    }

    // Ensure container is ready
    if (
      !swiperContainer.querySelector('.swiper-wrapper') ||
      !swiperContainer.querySelectorAll('.swiper-slide').length
    ) {
      setTimeout(() => this.initSwiper(retryCount + 1), 50);
      return;
    }

    try {
      this.swiperInstance = new window.swiper(swiperContainer, {
        slidesPerView: 'auto',
        spaceBetween: 8,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        allowTouchMove: true,
        on: {
          init: () => {
            this.addCartButtonListeners();
          }
        }
      });
    } catch (error) {
      this.swiperInstance = null;
      this.addCartButtonListeners();
    }
  }

  addCartButtonListeners() {
    if (!this.container) return;
    
    // Add to Cart forms
    const addForms = this.container.querySelectorAll('.cart-drawer-product-form');
    if (!this.handleFormSubmitBound) {
      this.handleFormSubmitBound = this.handleFormSubmit.bind(this);
    }
    addForms.forEach(form => {
      form.removeEventListener('submit', this.handleFormSubmitBound);
      form.addEventListener('submit', this.handleFormSubmitBound);
    });
  }

  handleFormSubmit(event) {
    event.preventDefault();
    this.addToCartViaForm(event.currentTarget);
  }

  preserveState() {
    if (this.container) {
      const swiperContainer = this.container.querySelector('.cart-drawer-recommendations-swiper');
      const hasSwiperContainer = swiperContainer && swiperContainer.querySelector('.swiper-wrapper') && swiperContainer.querySelectorAll('.swiper-slide').length > 0;
      
      this.preservedState = {
        cartProductIds: this.container.dataset.cartProductIds,
        lastCartProductIds: this.lastCartProductIds,
        hasLoadedRecommendations: this.hasLoadedRecommendations,
        productsVisible: this.productsContainer?.style.display !== 'none',
        fallbackVisible: this.fallbackContainer?.style.display !== 'none',
        hasSwiperContainer: hasSwiperContainer
      };
    }
  }

  restoreState() {
    if (this.preservedState && this.container) {
      // Restore the cart product IDs
      this.container.dataset.cartProductIds = this.preservedState.cartProductIds;
      this.lastCartProductIds = this.preservedState.lastCartProductIds || '';
      
      // Restore the loaded state
      this.hasLoadedRecommendations = this.preservedState.hasLoadedRecommendations;
      
      // Only restore visibility if there are cart items
      if (this.preservedState.cartProductIds && this.preservedState.cartProductIds.trim() !== '') {
        // Show recommendations container since cart has items
        this.container.style.display = 'block';
        
        if (this.preservedState.productsVisible && this.preservedState.hasSwiperContainer) {
          this.showProducts();
        } else if (this.preservedState.fallbackVisible) {
          this.showFallback();
        } else {
          // If products were visible but Swiper container is missing, reload recommendations
          this.hasLoadedRecommendations = false;
          setTimeout(() => {
            if (!this.isLoading) {
              this.loadRecommendations();
            }
          }, 100);
        }
      } else {
        // If no cart items, always hide recommendations regardless of preserved state
        this.hideRecommendations();
        this.hasLoadedRecommendations = false; // Reset to allow reloading when items are added
      }
      
      // Clear preserved state
      this.preservedState = null;
    }
  }

  reinitialize() {
    // Preserve current state before re-initialization
    this.preserveState();
    
    // Reset initialization flag to allow re-initialization
    this.isInitialized = false;
    
    // Re-find container elements in case they were re-rendered
    this.container = document.querySelector('.cart-drawer-recommendations');
    this.loadingContainer = this.container?.querySelector('.cart-drawer-recommendations__loading');
    this.productsContainer = this.container?.querySelector('.cart-drawer-recommendations__products');
    this.fallbackContainer = this.container?.querySelector('.cart-drawer-recommendations__fallback');
    
    if (this.container) {
      // Restore preserved state
      this.restoreState();
      
      // Check if cart has items and load recommendations accordingly
      const cartProductIds = this.container.dataset.cartProductIds;
      
      if (cartProductIds && cartProductIds.trim() !== '') {
        // Initialize the last cart product IDs if not already set
        if (!this.lastCartProductIds) {
          this.lastCartProductIds = cartProductIds;
        }
        
        // Show recommendations container since cart has items
        this.container.style.display = 'block';
        
        // Check if recommendations are already visible and properly rendered
        const isAlreadyVisible = this.isRecommendationsVisible();
        
        if (isAlreadyVisible) {
          // Just reinitialize Swiper if recommendations are already visible
          setTimeout(() => {
            this.initSwiper();
          }, 100);
        } else {
          // Reset the loaded flag to allow reloading
          this.hasLoadedRecommendations = false;
          // Small delay to ensure DOM is fully updated
          setTimeout(() => {
            // Only load if not already loading
            if (!this.isLoading) {
              this.loadRecommendations();
            }
          }, 100);
        }
      } else {
        this.hideRecommendations();
      }
    }
  }

  // Method to re-initialize just Swiper and event listeners without reloading recommendations
  reinitializeSwiperAndListeners() {
    // Re-find container elements in case they were re-rendered
    this.container = document.querySelector('.cart-drawer-recommendations');
    this.loadingContainer = this.container?.querySelector('.cart-drawer-recommendations__loading');
    this.productsContainer = this.container?.querySelector('.cart-drawer-recommendations__products');
    this.fallbackContainer = this.container?.querySelector('.cart-drawer-recommendations__fallback');
    
    if (this.container) {
      // Check if recommendations are visible and properly rendered
      const swiperContainer = this.container.querySelector('.cart-drawer-recommendations-swiper');
      const hasSwiperContainer = swiperContainer && swiperContainer.querySelector('.swiper-wrapper') && swiperContainer.querySelectorAll('.swiper-slide').length > 0;
      const isVisible = (this.productsContainer?.style.display !== 'none' || 
                       this.fallbackContainer?.style.display !== 'none') && hasSwiperContainer;
      
      if (isVisible) {
        this.initSwiper();
        setTimeout(() => {
          this.addCartButtonListeners();
        }, 100);
        // Reset the quantity-only change flag after reinitializing
        this.isQuantityOnlyChange = false;
      } else if (this.isQuantityOnlyChange) {
        // Don't reload recommendations for quantity changes
        this.isQuantityOnlyChange = false; // Reset the flag
      }
    }
  }

  // Method to reset any stuck buttons after cart updates
  resetStuckButtons() {
    if (!this.container) return;
    
    // Reset stuck form buttons
    const stuckFormButtons = this.container.querySelectorAll('.cart-drawer-product-form button[type="submit"]');
    stuckFormButtons.forEach(button => {
      if (button.textContent === 'Adding...' && button.disabled) {
        button.textContent = 'Add';
        button.disabled = false;
      }
    });
    
    // Also check for any legacy button selectors
    const stuckButtons = this.container.querySelectorAll('.cart-drawer-product-card__add');
    stuckButtons.forEach(button => {
      if (button.textContent === 'Adding...' && button.disabled) {
        button.textContent = 'Add';
        button.disabled = false;
      }
    });
  }

  async addToCartViaForm(form) {
    const variantId = form.querySelector('input[name="id"]').value;
    const submitButton = form.querySelector('button[type="submit"]');
    
    if (!variantId) {
      console.error('CartDrawerRecommendations: No variant ID found in form');
      return;
    }

    // Check if item is already in cart
    const isAlreadyInCart = await this.checkIfItemInCart(variantId);
    if (isAlreadyInCart) {
      // Update quantity instead of adding new item
      await this.updateItemQuantity(variantId, 1);
      return;
    }

    // Prevent duplicate submissions
    if (submitButton.disabled && submitButton.textContent === 'Adding...') {
      return;
    }

    // Disable button and show loading state
    submitButton.disabled = true;
    submitButton.textContent = 'Adding...';

    try {
      // Get the cart drawer element
      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer) {
        console.error('CartDrawerRecommendations: Cart drawer not found');
        throw new Error('Cart drawer not found');
      }

      // Use the same approach as product form
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(form);
      
      // Add sections for cart drawer update (same as product form)
      if (cartDrawer) {
        const sectionsToRender = cartDrawer.getSectionsToRender();
        
        // Convert sections to comma-separated string (same format as our previous approach)
        const sectionIds = sectionsToRender.map((section) => section.id).join(',');
        
        formData.append('sections', sectionIds);
        formData.append('sections_url', window.location.pathname);
        cartDrawer.setActiveElement(document.activeElement);
      }
      
      config.body = formData;

      const response = await fetch(`${window.routes.cart_add_url}`, config);

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      const cartData = await response.json();
      
      if (cartData.status) {
        // Handle error
        throw new Error(cartData.description || 'Failed to add to cart');
      }

      // Use the same cart update system as product form
      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-drawer-recommendations',
          productVariantId: variantId,
          cartData: cartData,
        });
      } else {
        // Fallback to custom event if pub/sub is not available
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { cart: cartData }
        }));
      }
      
      // Reset button state after successful completion
      submitButton.textContent = 'Add';
      submitButton.disabled = false;

    } catch (error) {
      console.error('CartDrawerRecommendations: Error in addToCartViaForm', {
        error: error.message,
        stack: error.stack,
        variantId
      });
      
      submitButton.textContent = 'Error';
      setTimeout(() => {
        submitButton.textContent = 'Add';
        submitButton.disabled = false;
      }, 2000);
    }
    
    // Always reset button state after completion (success or error)
    setTimeout(() => {
      // Find the button again in case the DOM was re-rendered
      const currentButton = this.container?.querySelector(`form[action*="cart/add"] button[type="submit"]`);
      if (currentButton && currentButton.textContent === 'Adding...') {
        currentButton.textContent = 'Add';
        currentButton.disabled = false;
      }
    }, 100);
  }

  async checkIfItemInCart(variantId) {
    try {
      // Get current cart state
      const response = await fetch('/cart.js');
      if (!response.ok) {
        console.error('CartDrawerRecommendations: Failed to fetch cart state');
        return false;
      }
      
      const cart = await response.json();
      
      // Check if variant is already in cart
      const isInCart = cart.items?.some(item => item.variant_id.toString() === variantId.toString());
      
      return isInCart;
    } catch (error) {
      console.error('CartDrawerRecommendations: Error checking cart state', error);
      return false;
    }
  }

  async updateItemQuantity(variantId, quantity) {
    try {
      // Get current cart state
      const response = await fetch('/cart.js');
      if (!response.ok) {
        console.error('CartDrawerRecommendations: Failed to fetch cart state');
        return;
      }
      
      const cart = await response.json();
      
      // Find the item to update
      const itemToUpdate = cart.items?.find(item => item.variant_id.toString() === variantId.toString());
      if (!itemToUpdate) {
        console.error('CartDrawerRecommendations: Item not found in cart');
        return;
      }
      
      // Update the quantity
      itemToUpdate.quantity = quantity;
      
      // Send the updated cart state back to Shopify
      const updateResponse = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          updates: {
            [variantId]: quantity
          }
        })
      });

      if (!updateResponse.ok) {
        console.error('CartDrawerRecommendations: Failed to update cart');
        return;
      }

      const updatedCart = await updateResponse.json();
      
      // Trigger cart update event for other components
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cart: updatedCart }
      }));

    } catch (error) {
      console.error('CartDrawerRecommendations: Error updating item quantity', error);
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CartDrawerRecommendations();
  });
} else {
  new CartDrawerRecommendations();
}

// Export for use in other scripts
window.CartDrawerRecommendations = CartDrawerRecommendations; 